import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Phone, Mountain, MapPin, Users } from "lucide-react";

const PHOTOS = [
  { src: "https://dispatchhunts.com/wp-content/uploads/2024/12/Brown-Bear-Alaska-3-1.jpg", caption: "Alaska — Brown Bear Camp" },
  { src: "https://dispatchhunts.com/wp-content/uploads/2024/12/Tajikistan-Marco-Polo.jpg", caption: "Tajikistan — Marco Polo" },
  { src: "https://dispatchhunts.com/wp-content/uploads/2024/12/Elk-Hunt-Montana-3.jpg", caption: "Montana — Elk" },
  { src: "https://dispatchhunts.com/wp-content/uploads/2024/12/Brown-Bear-Kamchatka.jpg", caption: "Kamchatka — Brown Bear" },
  { src: "https://dispatchhunts.com/wp-content/uploads/2025/03/2b3bd60b-3821-40e2-b178-75f16c50775e.jpeg", caption: "South Africa — Safari" },
  { src: "https://dispatchhunts.com/wp-content/uploads/2025/10/IMG_2826.jpeg", caption: "Pakistan — Himalayan Ibex" },
];

const PRINCIPLES = [
  { title: "Straight talk.", text: "You'll get honest advice, not a pitch. If a hunt isn't right for you, we'll say so. If the timing's wrong or the outfitter isn't delivering, you'll hear it first." },
  { title: "Boots on the ground.", text: "Every outfitter in our network has been personally vetted. Our team has been to the camps, met the guides, and evaluated the operation. That's not marketing — it's how we work." },
  { title: "No surprises.", text: "Before you book, you'll know exactly what to expect. The terrain. The physical demands. What's included and what's not. The real cost, not a teaser price." },
  { title: "Your hunt.", text: "We work for you, not the outfitter. Our job is to match you with the right opportunity and make sure every detail is handled so you can focus on the hunt." },
];

const TEAM_STRENGTHS = [
  { icon: Mountain, title: "Six Continents", text: "Our advisors have hunted everywhere we send clients. From the Pamirs to the Alaska Peninsula to the Eastern Cape — we've been on the ground." },
  { icon: MapPin, title: "200+ Expeditions", text: "Collectively, our team has coordinated over 200 expeditions. Every outfitter is personally vetted. Every operation evaluated firsthand." },
  { icon: Users, title: "Personalized Guidance", text: "You'll work directly with an experienced advisor who knows the terrain, the outfitter, and the species. Not a call center — a real conversation with someone who's been there." },
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>About Our Team | Dispatch Hunts</title>
        <meta name="description" content="Dispatch Hunts is built by experienced guides and advisors who've spent 15+ years in the field across six continents. Personalized guidance for serious hunters." />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end" data-testid="about-hero">
        <div className="absolute inset-0">
          <img src="https://dispatchhunts.com/wp-content/uploads/2024/12/home-page-brown-bear-Matt.jpg" alt="Dispatch Hunts team in the field" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/60 to-transparent" />
        </div>
        <div className="container-dh relative z-10 pb-16 pt-32">
          <p className="text-label text-brass mb-4">About Dispatch Hunts</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-bone max-w-2xl leading-tight" data-testid="about-headline">
            Experienced guides who've been where you're going.
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="section-light py-20 lg:py-28" data-testid="about-story">
        <div className="container-dh">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            <div className="lg:col-span-3 space-y-6 font-body text-slate leading-relaxed">
              <p className="text-editorial text-carbon text-2xl leading-relaxed">
                Dispatch Hunts was built in the field, not behind a desk. Our team of advisors has spent a combined 30+ years guiding, hunting, and coordinating expeditions across six continents.
              </p>
              <p>
                We started this because we saw the same problem over and over: serious hunters spending serious money on hunts they shouldn't have booked, with outfitters who weren't delivering, in places that didn't match what they were told.
              </p>
              <p>
                The brokerage industry is full of middlemen who've never spent a night in a wall tent. Our team has. We've glassed sheep at 14,000 feet in the Pamirs, packed out brown bears on the Alaska Peninsula, and sat in ground blinds in the Eastern Cape. We know what a good outfitter looks like because we've been on both sides of the operation.
              </p>
              <p>
                Dispatch Hunts exists to close the gap between what hunters are told and what they actually get. We personally vet every outfitter, handle the logistics, and stay involved from the first conversation to the post-hunt debrief.
              </p>
              <p>
                No sales team. No call center. No automated funnel. Just a direct line to experienced advisors who've been there and will tell you straight.
              </p>
            </div>
            <div className="lg:col-span-2">
              <img src="https://dispatchhunts.com/wp-content/uploads/2024/12/about-picture-of-Matts.jpg" alt="Dispatch Hunts founder" className="w-full aspect-[3/4] object-cover" loading="lazy" />
              <div className="mt-4 p-5 bg-chalk">
                <p className="font-display text-lg font-bold text-carbon">Matt Burgess</p>
                <p className="text-sm text-smoke font-body">Founder & Lead Advisor</p>
                <p className="text-sm text-smoke font-body mt-1">Kalispell, Montana</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Strengths */}
      <section className="section-canvas py-20 lg:py-28" data-testid="about-strengths">
        <div className="container-dh">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {TEAM_STRENGTHS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="text-center">
                <div className="w-14 h-14 bg-brass/10 flex items-center justify-center mx-auto mb-5">
                  <Icon className="w-6 h-6 text-brass" />
                </div>
                <h3 className="font-display text-xl font-bold text-carbon mb-3">{title}</h3>
                <p className="font-body text-sm text-smoke leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-dark grain py-20 lg:py-28" data-testid="about-philosophy">
        <div className="container-dh">
          <p className="text-label text-brass mb-3">How We Work</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-bone mb-14 max-w-lg">
            Four principles. No exceptions.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {PRINCIPLES.map((p) => (
              <div key={p.title} className="border-l-2 border-brass/40 pl-6">
                <h3 className="font-display text-xl font-bold text-bone mb-2">{p.title}</h3>
                <p className="font-body text-sm text-bone/60 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Field Photos */}
      <section className="section-chalk py-20 lg:py-28" data-testid="about-photos">
        <div className="container-dh">
          <p className="text-label text-brass mb-3">From the Field</p>
          <h2 className="font-display text-3xl font-bold text-carbon mb-10">Where we've been. Hunts we've coordinated.</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {PHOTOS.map((p) => (
              <div key={p.caption} className="relative group overflow-hidden aspect-[4/3]">
                <img src={p.src} alt={p.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-carbon/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <p className="absolute bottom-3 left-3 text-label text-bone text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">{p.caption}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark grain py-20" data-testid="about-cta">
        <div className="container-dh text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-bone mb-4">Ready to talk about your next hunt?</h2>
          <p className="font-body text-bone/50 mb-8">Our advisors personally handle every consultation. No sales reps. No runaround.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/apply" className="btn-brass" data-testid="about-apply-btn">Apply for a Consultation <ArrowRight className="w-4 h-4" /></Link>
            <a href="tel:4065805366" className="btn-outline" data-testid="about-call-btn"><Phone className="w-4 h-4" /> 406-580-5366</a>
          </div>
        </div>
      </section>
    </>
  );
}
